import { useState, useEffect } from "react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const AVATAR_COLORS = [
  "#9FFF00", "#16A34A", "#EA580C", "#8B5CF6", "#DC2626",
  "#0891B2", "#CA8A04", "#DB2777", "#4F46E5", "#059669",
];

export const InfluencerAvatar = ({ name, profiles, size = "md", className = "" }) => {
  const avatarUrls = [];
  const platformMap = { tiktok: "tiktok", instagram: "instagram", youtube: "youtube", twitter: "x" };

  if (profiles?.length > 0) {
    for (const p of profiles) {
      const verified = p.raw_data?.verified_avatar;
      if (verified) {
        avatarUrls.push(verified);
        break;
      }
    }

    const sorted = [...profiles].sort((a, b) => (b.followers || 0) - (a.followers || 0));

    // 1) Scraped IG CDN URL via our backend proxy (bypasses CORS/hotlink).
    for (const p of sorted) {
      const directAvatar = p.avatar_url;
      if (directAvatar && !directAvatar.includes("unavatar.io") && !avatarUrls.includes(directAvatar)) {
        const proxied = `${API}/outreach/avatar?url=${encodeURIComponent(directAvatar)}`;
        avatarUrls.push(proxied);
      }
    }

    // 2) Backend proxy by handle — reads from our scan DB cache (no client
    // IP quota, no CORS issues, serves the IG photo if we've ever scraped
    // it for this handle before).
    for (const p of sorted) {
      const plat = platformMap[p.platform];
      const handle = p.username?.replace("@", "").toLowerCase();
      if (plat === "instagram" && handle) {
        avatarUrls.push(`${API}/outreach/avatar?handle=${encodeURIComponent(handle)}`);
      }
    }

    // 3) unavatar.io — last resort; has 50/day limit per client IP but
    // works for end-user browsers that haven't hit the cap yet.
    for (const p of sorted) {
      const plat = platformMap[p.platform];
      const handle = p.username?.replace("@", "").toLowerCase();
      if (plat && handle) {
        avatarUrls.push(`https://unavatar.io/${plat}/${handle}?fallback=false`);
      }
    }

    for (const p of profiles) {
      const wikiPhoto = p.raw_data?.wiki_photo;
      if (wikiPhoto) {
        avatarUrls.push(wikiPhoto);
        break;
      }
    }
  }

  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    setCurrentIdx(0);
  }, [name]);

  const sizeClasses = {
    sm: "w-12 h-12 text-sm",
    md: "w-16 h-16 text-base",
    lg: "w-20 h-20 text-lg",
  }[size] || "w-16 h-16 text-base";

  const initials = (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join("");

  const colorIdx = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length;
  const currentUrl = avatarUrls[currentIdx];

  if (currentUrl) {
    return (
      <img
        src={currentUrl}
        alt={name}
        // IG CDN and many avatar hosts block hotlinks via the Referer
        // header. `no-referrer` strips it so the image loads reliably.
        referrerPolicy="no-referrer"
        className={`${sizeClasses} object-cover border border-black/10 ${className}`}
        onError={() => {
          if (currentIdx + 1 < avatarUrls.length) {
            setCurrentIdx(currentIdx + 1);
          } else {
            setCurrentIdx(-1);
          }
        }}
        onLoad={(e) => {
          if (e.target.naturalWidth > 0 && e.target.naturalWidth < 100) {
            if (currentIdx + 1 < avatarUrls.length) {
              setCurrentIdx(currentIdx + 1);
            }
          }
        }}
        data-testid="influencer-avatar"
      />
    );
  }

  return (
    <div
      className={`${sizeClasses} flex items-center justify-center font-bold text-white border border-black/10 ${className}`}
      style={{ backgroundColor: AVATAR_COLORS[colorIdx] }}
      data-testid="influencer-avatar"
    >
      {initials}
    </div>
  );
};
